/**
 * us-consent-gate.js: one place to ask "may we track this visitor?", and the
 * code that makes the vendors Shopify's consent cannot reach honour the answer.
 *
 * Ported from the Murphy Door theme's md-consent-gate.js. Measured on the live
 * store 2026-10-09 (Utah, no banner shown, saleOfDataRegion true): with consent
 * fully declined, or with a sale opt-out / Global Privacy Control, Google Ads
 * remarketing still fired with npa=0, 10xScaleCRM loaded parser-blocking and
 * posted to leadconnectorhq, and Clarity kept recording. Shopify's own consent
 * only reaches Web Pixels and the apps that read it; everything below closes
 * the rest from the theme. See .claude/docs/known-footguns.md, "Consent gating".
 *
 * FAILURE DIRECTIONS (deliberate, and different per helper):
 * - marketingAllowed / analyticsAllowed / saleOfDataAllowed fail OPEN when the
 *   Customer Privacy API is missing: a subtly wrong gate silently kills tracking
 *   with no console error. GPC still forces marketing and sale to false.
 * - replayAllowed fails CLOSED: session replay is the largest privacy surface,
 *   so with no real answer from Shopify, Clarity is never loaded.
 * - Clarity is LOADED by this file (loadClarity), not by the app embed: it is
 *   injected only once replayAllowed() is true, so before that nothing is sent.
 * - The 10xScale loader needs an authoritative yes: it was ungated before, and
 *   it reads form fields, so it never loads on a guess.
 * - syncConsentMode stays SILENT unless Shopify answered: pushing "granted" on a
 *   guess would override a future regional deny, and "denied" on a guess would
 *   kill Google tracking for every visitor.
 */
(function () {
  'use strict';

  var MIRROR_COOKIE = 'us_cmirror';
  var MIRROR_MAX_AGE = 31536000; // one year, same as Shopify's consent cookie
  var TENX_SRC = 'https://link.10xscalecrm.com/js/external-tracking.js';
  var TENX_TRACKING_ID = 'tk_764c8de580934b759bd9e98323b4bbcd';
  var CLARITY_PROJECT_ID = 'xx776cv8ql'; // read from the live clarity_js embed + web pixel config
  var CLARITY_POLL_MS = 500;
  var CLARITY_POLL_MAX = 40; // ~20s for a late Clarity embed
  var API_FALLBACK_MS = 4000; // if the privacy API never answers, still gate replay

  function cp() {
    try {
      return (window.Shopify && window.Shopify.customerPrivacy) || null;
    } catch (_) {
      return null;
    }
  }

  /* Global Privacy Control: a sale/share opt-out honoured by CA, CO, CT, DE,
     MD, MN, MT, NH, NJ, OR and TX. Ad pixels are "sharing", so GPC turns off
     marketing and sale whatever the consent API reports. */
  function gpcOn() {
    try {
      return !!(
        window.navigator && window.navigator.globalPrivacyControl === true
      );
    } catch (_) {
      return false;
    }
  }

  function allowed(method) {
    if (
      gpcOn() &&
      (method === 'marketingAllowed' || method === 'saleOfDataAllowed')
    ) {
      return false;
    }
    var api = cp();
    if (!api || typeof api[method] !== 'function') return true; // fail OPEN
    try {
      return api[method]() !== false;
    } catch (_) {
      return true; // fail OPEN
    }
  }

  function isAuthoritative() {
    var api = cp();
    return !!(api && typeof api.marketingAllowed === 'function');
  }

  function marketingAllowed() {
    return allowed('marketingAllowed');
  }

  function analyticsAllowed() {
    return allowed('analyticsProcessingAllowed');
  }

  function saleOfDataAllowed() {
    return allowed('saleOfDataAllowed');
  }

  /** Session replay (Clarity). Fails CLOSED when Shopify has not answered. */
  function replayAllowed() {
    try {
      if (!isAuthoritative()) return false;
      return analyticsAllowed() && saleOfDataAllowed() && !gpcOn();
    } catch (_) {
      return false;
    }
  }

  /** Ads may use the visitor's data only with marketing consent AND no sale opt-out. */
  function adsAllowed() {
    return marketingAllowed() && saleOfDataAllowed();
  }

  function gtagFn() {
    if (typeof window.gtag === 'function') return window.gtag;
    // gtag.js only accepts Arguments objects from the queue, never arrays.
    window.dataLayer = window.dataLayer || [];
    return function () {
      window.dataLayer.push(arguments);
    };
  }

  /* ------------------------------------------------------------------------
     GOOGLE CONSENT MODE v2, the `update` half. The Google & YouTube app pushes
     `consent default` all-granted and never denies ad_user_data /
     ad_personalization on a sale opt-out, so remarketing kept npa=0. This
     pushes the visitor's real state. ad_* are denied when marketing is off OR
     the visitor opted out of sale/sharing (GPC included).
     ------------------------------------------------------------------------ */
  function syncConsentMode() {
    try {
      if (!isAuthoritative()) return null;
      var ads = adsAllowed() ? 'granted' : 'denied';
      var payload = {
        ad_storage: marketingAllowed() ? 'granted' : 'denied',
        ad_user_data: ads,
        ad_personalization: ads,
        analytics_storage: analyticsAllowed() ? 'granted' : 'denied',
      };
      gtagFn()('consent', 'update', payload);
      return payload;
    } catch (_) {
      return null; // a consent push must never break the page
    }
  }

  /* ------------------------------------------------------------------------
     RE-ASSERT DENIALS after the Google & YouTube app. On the page where the
     visitor opts out (S2), our update landed at ~+1837ms and the app's
     pixel.modern.js pushed an all-granted `update` at ~+1841ms, so Google
     ended that page fully granted (UAT 2026-10-10). The next page is fine:
     snippets/us-consent-mode-mirror.liquid denies before the app runs.
     So after every sync, re-push the current DENIALS at +250ms, +1s and +3s,
     and scan window.dataLayer for a later `consent update` that grants a key
     we deny, re-pushing at once. Read-only scan: no wrapped push, no
     prototype touched. Only denials that match the visitor's CURRENT consent
     are ever pushed, recomputed at fire time; this never grants anything.
     ------------------------------------------------------------------------ */
  var REASSERT_DELAYS = [250, 1000, 3000];
  var DL_SCAN_MS = 250;
  var DL_SCAN_MAX = 40; // ~10s after each sync
  var reassertTimers = [];
  var dlScanTimer = null;
  var dlSeen = 0;

  /** The denied subset of the Consent Mode state, or null if nothing is denied. */
  function consentDenials() {
    if (!isAuthoritative()) return null;
    var ads = adsAllowed();
    var d = {};
    var any = false;
    if (!marketingAllowed()) {
      d.ad_storage = 'denied';
      any = true;
    }
    if (!ads) {
      d.ad_user_data = 'denied';
      d.ad_personalization = 'denied';
      any = true;
    }
    if (!analyticsAllowed()) {
      d.analytics_storage = 'denied';
      any = true;
    }
    return any ? d : null;
  }

  function reassertDenials() {
    try {
      var d = consentDenials();
      if (d) gtagFn()('consent', 'update', d);
      return d;
    } catch (_) {
      return null;
    }
  }

  /** True if a dataLayer entry is a `consent update` granting a key in `d`. */
  function grantsDenied(entry, d) {
    try {
      if (!entry || entry[0] !== 'consent' || entry[1] !== 'update') {
        return false;
      }
      var p = entry[2];
      if (!p || typeof p !== 'object') return false;
      for (var k in d) {
        if (p[k] === 'granted') return true;
      }
    } catch (_) {}
    return false;
  }

  function scanDataLayer() {
    var dl = window.dataLayer;
    if (!dl || typeof dl.length !== 'number') return;
    var start = dlSeen;
    dlSeen = dl.length;
    var d = consentDenials();
    if (!d) return;
    for (var i = start; i < dl.length; i++) {
      if (grantsDenied(dl[i], d)) {
        reassertDenials();
        dlSeen = window.dataLayer.length; // skip our own push
        return;
      }
    }
  }

  function scheduleReasserts() {
    try {
      for (var i = 0; i < reassertTimers.length; i++) {
        clearTimeout(reassertTimers[i]);
      }
      reassertTimers = [];
      if (dlScanTimer) clearInterval(dlScanTimer);
      dlScanTimer = null;
      if (!consentDenials()) return;
      for (var j = 0; j < REASSERT_DELAYS.length; j++) {
        reassertTimers.push(setTimeout(reassertDenials, REASSERT_DELAYS[j]));
      }
      dlSeen = (window.dataLayer && window.dataLayer.length) || 0;
      var tries = 0;
      dlScanTimer = setInterval(function () {
        tries++;
        try {
          scanDataLayer();
        } catch (_) {}
        if (tries >= DL_SCAN_MAX) {
          clearInterval(dlScanTimer);
          dlScanTimer = null;
        }
      }, DL_SCAN_MS);
    } catch (_) {}
  }

  /* First-party mirror of the last authoritative answer, read by the inline
     snippets/us-consent-mode-mirror.liquid on the NEXT page so it can deny
     Consent Mode before the Google & YouTube app's tags initialise. Flags:
     a = analytics, m = marketing, s = sale of data (false under GPC). */
  function mirrorValue() {
    return (
      'a' +
      (analyticsAllowed() ? 1 : 0) +
      'm' +
      (marketingAllowed() ? 1 : 0) +
      's' +
      (saleOfDataAllowed() ? 1 : 0)
    );
  }

  function readMirror() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)us_cmirror=([^;]*)/);
      return m ? m[1] : null;
    } catch (_) {
      return null;
    }
  }

  function writeMirror() {
    try {
      if (!isAuthoritative()) return null;
      var value = mirrorValue();
      if (readMirror() === value) return value;
      var secure =
        window.location && window.location.protocol === 'https:'
          ? '; Secure'
          : '';
      document.cookie =
        MIRROR_COOKIE +
        '=' +
        value +
        '; path=/; max-age=' +
        MIRROR_MAX_AGE +
        '; SameSite=Lax' +
        secure;
      return value;
    } catch (_) {
      return null;
    }
  }

  /* ------------------------------------------------------------------------
     MICROSOFT CLARITY. The app embed (clarity_js, now disabled; loadClarity
     below replaces it) recorded regardless of Shopify consent: 15+ collect
     calls after a full decline, and it pushed its own consentv2 with analytics
     granted on a sale opt-out. consentv2 tells
     Clarity the visitor's state, but DENIED IS NOT A STOP: Clarity restarts
     cookieless and keeps uploading to clarity.ms/collect (UAT 2026-10-10, S2
     and S4). Only clarity('stop') halts collection; clarity('start') resumes.
     Read from the clarity-js 0.8.72 source (src/clarity.ts, src/queue.ts,
     src/data/metadata.ts), where both are exported and window.clarity(method)
     dispatches them:
     - A stopped Clarity swaps window.clarity back to a queue stub, so every
       later call (the app embed's consentv2 included) just queues. Only
       'start' restarts it: stop is a latch until we lift it.
     - Its tag loader moves its own 'start' to the FRONT of a pre-load queue,
       so a 'stop' queued before the tag script loads still runs after start.
     - consentv2 denied on a running, tracking Clarity does stop() then a timed
       start() itself; stopping FIRST means that restart is never scheduled.
     - clarity('consent', false) is consentv2 all-denied (APIv1): not a stop.
     Idempotent: stop/start fire only on a change, and 'start' first drops any
     'stop' this gate queued so it is not replayed after the restart.
     ------------------------------------------------------------------------ */
  function clarityPayload() {
    var authoritative = isAuthoritative();
    return {
      ad_Storage: authoritative && adsAllowed() ? 'granted' : 'denied',
      analytics_Storage: replayAllowed() ? 'granted' : 'denied',
    };
  }

  var clarityStopped = false;
  var clarityQueuedStops = [];
  var clarityLastKey = null;

  function clarityStop() {
    window.clarity('stop');
    var q = window.clarity && window.clarity.q;
    if (q && q.length) clarityQueuedStops.push(q[q.length - 1]);
    clarityStopped = true;
  }

  function clarityStart() {
    var q = window.clarity && window.clarity.q;
    if (q && q.length && clarityQueuedStops.length) {
      for (var i = q.length - 1; i >= 0; i--) {
        if (clarityQueuedStops.indexOf(q[i]) !== -1) q.splice(i, 1);
      }
    }
    clarityQueuedStops = [];
    window.clarity('start');
    clarityStopped = false;
  }

  function syncClarity(force) {
    try {
      if (typeof window.clarity !== 'function') return null;
      var payload = clarityPayload();
      var replay = payload.analytics_Storage === 'granted';
      var key = payload.ad_Storage + '|' + payload.analytics_Storage;
      if (force !== true && key === clarityLastKey) return payload;
      clarityLastKey = key;
      if (!replay && (force === true || !clarityStopped)) clarityStop();
      else if (replay && clarityStopped) clarityStart();
      window.clarity('consentv2', payload);
      return payload;
    } catch (_) {
      return null;
    }
  }

  /* ------------------------------------------------------------------------
     CLARITY LOADER. The clarity_js app embed is DISABLED in
     config/settings_data.json: it loaded the tag before this deferred gate
     ran, so Clarity recorded first and the stop itself flushed a snapshot
     (re-UAT 2026-10-10, S2 and S4). This injects the standard Clarity snippet
     only once replayAllowed() is true, after the consent API answers or on
     visitorConsentCollected. Before that window.clarity does not exist and
     nothing is sent. Idempotent: one tag per page, and it stands down if a
     Clarity tag is already on the page. A later withdrawal is handled by
     syncClarity's stop/start latch above.
     ------------------------------------------------------------------------ */
  var clarityLoaded = false;
  function loadClarity() {
    try {
      if (clarityLoaded) return false;
      if (!replayAllowed()) return false;
      if (document.querySelector('script[src*="clarity.ms/tag/"]')) {
        clarityLoaded = true;
        return false;
      }
      (function (c, l, a, r, i, t, y) {
        c[a] =
          c[a] ||
          function () {
            (c[a].q = c[a].q || []).push(arguments);
          };
        t = l.createElement(r);
        t.async = true;
        t.src = 'https://www.clarity.ms/tag/' + i;
        y = l.getElementsByTagName(r)[0];
        if (y && y.parentNode) y.parentNode.insertBefore(t, y);
        else (l.head || l.documentElement).appendChild(t);
      })(window, document, 'clarity', 'script', CLARITY_PROJECT_ID);
      clarityLoaded = true;
      return true;
    } catch (_) {
      return false;
    }
  }

  /* Re-run when Clarity appears, and keep watching: a live Clarity exposes
     window.clarity.v (a stopped or not-yet-loaded one is a stub without it),
     so .v while replay is denied means it is RECORDING. Force the stop. */
  var clarityTimer = null;
  function watchClarity() {
    if (clarityTimer) return;
    var tries = 0;
    clarityTimer = setInterval(function () {
      tries++;
      try {
        if (ready && typeof window.clarity === 'function') {
          if (clarityLastKey === null) syncClarity();
          else if (window.clarity.v && !replayAllowed()) syncClarity(true);
        }
      } catch (_) {}
      if (tries >= CLARITY_POLL_MAX) {
        clearInterval(clarityTimer);
        clarityTimer = null;
      }
    }, CLARITY_POLL_MS);
  }

  /* ------------------------------------------------------------------------
     10xSCALECRM. Was a parser-blocking, ungated <script> in theme.liquid that
     posts to backend.leadconnectorhq.com and reads form fields. Now injected
     only on an authoritative yes to marketing AND sale of data (so never under
     a decline, a sale opt-out or GPC). The vendor script reads its config from
     document.currentScript and handles a load after DOMContentLoaded.
     ------------------------------------------------------------------------ */
  var tenxLoaded = false;
  function loadTenX() {
    try {
      if (tenxLoaded) return false;
      if (!(isAuthoritative() && marketingAllowed() && saleOfDataAllowed()))
        return false;
      if (
        document.querySelector(
          'script[src*="10xscalecrm.com/js/external-tracking"]'
        )
      ) {
        tenxLoaded = true;
        return false;
      }
      var s = document.createElement('script');
      s.src = TENX_SRC;
      s.async = true;
      s.setAttribute('data-tracking-id', TENX_TRACKING_ID);
      (document.head || document.documentElement).appendChild(s);
      tenxLoaded = true;
      return true;
    } catch (_) {
      return false;
    }
  }

  /* GPC write-back: record the signal through Shopify's own API, so every
     consent-aware app (Web Pixels, Klaviyo) honours it, not just this file.
     Shopify already sets sale_of_data to "no" by itself under GPC (measured
     2026-10-09), but leaves marketing unset, and Klaviyo kept tracking. So
     "no stored choice" means marketing is neither yes nor no; a visitor's
     explicit marketing answer is never overwritten. Sale/sharing only: GPC
     does not require switching off first-party analytics. */
  function recordGpcOptOut() {
    if (!gpcOn()) return false;
    try {
      var api = cp();
      if (!api || typeof api.setTrackingConsent !== 'function') return false;
      if (typeof api.currentVisitorConsent === 'function') {
        var c = api.currentVisitorConsent() || {};
        if (c.marketing === 'yes' || c.marketing === 'no') return false;
      }
      api.setTrackingConsent(
        { marketing: false, sale_of_data: false },
        function () {
          syncAll();
        }
      );
      return true;
    } catch (_) {
      return false;
    }
  }

  var hooks = [];
  var ready = false;

  /** Register fn to run after every authoritative consent read (load + changes). */
  function onConsent(fn) {
    if (typeof fn !== 'function') return;
    hooks.push(fn);
    if (ready) {
      try {
        fn(usConsent);
      } catch (_) {}
    }
  }

  function syncAll() {
    ready = true;
    writeMirror();
    syncConsentMode();
    scheduleReasserts();
    loadClarity();
    syncClarity();
    for (var i = 0; i < hooks.length; i++) {
      try {
        hooks[i](usConsent);
      } catch (_) {}
    }
  }

  /* CCPA regs 7025(c)(6): display that a GPC signal was honoured. */
  function showGpcHonored() {
    if (!gpcOn()) return 0;
    var added = 0;
    try {
      var links = document.querySelectorAll('[data-md-privacy-choices]');
      for (var i = 0; i < links.length; i++) {
        var next = links[i].nextElementSibling;
        if (next && next.hasAttribute('data-md-gpc-honored')) continue;
        var note = document.createElement('span');
        note.setAttribute('data-md-gpc-honored', '');
        note.textContent = ' (Opt-Out Request Honored)';
        links[i].insertAdjacentElement('afterend', note);
        added++;
      }
    } catch (_) {}
    return added;
  }

  var usConsent = {
    analyticsAllowed: analyticsAllowed,
    marketingAllowed: marketingAllowed,
    saleOfDataAllowed: saleOfDataAllowed,
    replayAllowed: replayAllowed,
    gpc: gpcOn,
    isAuthoritative: isAuthoritative,
    syncConsentMode: syncConsentMode,
    reassertDenials: reassertDenials,
    syncClarity: syncClarity,
    loadClarity: loadClarity,
    writeMirror: writeMirror,
    recordGpcOptOut: recordGpcOptOut,
    loadTenX: loadTenX,
    showGpcHonored: showGpcHonored,
    onConsent: onConsent,
    syncAll: syncAll,
  };

  window.usConsent = usConsent;

  onConsent(loadTenX);

  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (err) {
          if (!err) recordGpcOptOut();
          syncAll();
        }
      );
    }
  } catch (_) {}

  // If the API never answers, never load Clarity; stop one that is already here.
  setTimeout(function () {
    if (!ready) {
      ready = true;
      syncClarity();
    }
  }, API_FALLBACK_MS);
  watchClarity();

  try {
    document.addEventListener('visitorConsentCollected', function () {
      syncAll();
    });
  } catch (_) {}

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', showGpcHonored);
    } else {
      showGpcHonored();
    }
  } catch (_) {}

  /* The footer "Do Not Sell or Share" link opens Shopify's preferences dialog.
     Progressive enhancement: the href (/pages/data-sharing-opt-out) stays, so a
     missing or throwing banner API still lands on the opt-out page. Modified
     and middle clicks keep the browser's own behaviour. */
  try {
    document.addEventListener('click', function (e) {
      if (window.usConsent !== usConsent) return; // a re-evaluated copy stands down
      var link;
      try {
        link =
          e.target &&
          e.target.closest &&
          e.target.closest('[data-md-privacy-choices]');
      } catch (_) {
        return;
      }
      if (!link) return;
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      var banner = window.privacyBanner;
      if (banner && typeof banner.showPreferences === 'function') {
        try {
          var result = banner.showPreferences();
          if (result && typeof result.catch === 'function')
            result.catch(function () {});
          e.preventDefault();
        } catch (_) {
          /* fall through to the href */
        }
      }
    });
  } catch (_) {}
})();
